import { LitElement, html, css, classMap } from '@hh/global.js';
class HHInputWrapper extends LitElement {
  static properties = {
    animation: {},
    focused: {
      type: Boolean,
      reflect: true,
    },
    filled: {
      type: Boolean,
      reflect: true,
    },
    disabled: {
      type: Boolean,
      reflect: true,
    },
    error: {
      type: Boolean,
      reflect: true,
    },
    icon: {
      type: String,
    },
    clearable: {
      type: Boolean,
      reflect: true,
    },
    validate: {
      type: Boolean,
      reflect: true,
    },
    success: {
      type: Boolean,
      reflect: true,
    },
    revealPassword: {
      type: Boolean,
      reflect: true,
      attribute: 'reveal-password',
    },
    resize: {
      type: Boolean,
      reflect: true,
    },
    label: {},
  };

  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();

    this.$select = this.querySelector(`select[slot='input']`);
    this.$select && this.preselectSelectValue();

    this.checkIfPreFilled();

    if (this.$select && this.label !== 'stacked') {
      this.filled = true;
    }
  }

  firstUpdated() {
    this.checkIfPreFilled();

    if (this.input && this.input.disabled) {
      this.disabled = true;
    }

    if (this.errorSlot) {
      this.error = true;
    }
  }

  updated(changedProperties) {
    if (changedProperties.has('disabled')) {
      this.input.disabled = this.disabled;
    }
  }

  checkIfPreFilled(tries = 6) {
    if (this.filled) return;
    if (this.input?.value && !this.$select) {
      this.filled = true;
    } else if (tries > 0) {
      setTimeout(() => {
        this.checkIfPreFilled(tries - 1);
      }, 200);
    }
  }

  preselectSelectValue() {
    if (this.$select?.dataset?.default) {
      this.$select.value = this.$select?.dataset?.default;
    }
  }

  onChange(e) {
    if (this.validate) {
      this.handleValidation();
    }

    if (this.resize) {
      this.handleAutoResize(e);
    }

    if (this.animation === 'none' || this.label === 'stacked') {
      return;
    }
    this.filled = !!e.target.value;
  }

  onFocus(e) {
    if (this.animation === 'none') {
      return;
    }
    this.focused = true;
    if (this.input.type === 'date' && this.dataset?.future) {
      this.handleDisableDates();
    }
  }

  onFocusOut(e) {
    if (this.animation === 'none') {
      return;
    }
    this.focused = false;
  }

  onAutoFill({ animationName }) {
    if (animationName === 'onAutoFillStart') {
      this.filled = true;
    }
  }

  onIconClick(e) {
    e.preventDefault();
    if (this.clearable) {
      this.handleClear();
    }

    if (this.revealPassword) {
      this.handleRevealPassword();
    }
  }

  handleClear() {
    this.input.value = '';
    this.filled = false;
    this.input.focus();
    this.input.dispatchEvent(new Event('input'));
  }

  handleValidation() {
    if (!this.filled) {
      this.success = false;
      return;
    }

    // TODO: Might need to make this more specific
    this.success = this.input.validity.valid;
  }

  handleRevealPassword() {
    this.input.type = this.input.type === 'password' ? 'text' : 'password';
  }

  handleAutoResize(e) {
    if (!this.resize) return;
    const { target } = e;

    target.style.height = 0;
    this.style.height = 0;
    target.style.height = `${target.scrollHeight}px`;
    this.style.height = `${target.scrollHeight + 26}px`;
  }

  handleDisableDates() {
    const date = new Date();
    const today = date.toISOString().split('T')[0];
    this.input.min = today;
  }

  iconTemplate() {
    return html`
      <slot
        name="clear-icon"
        class="${classMap({ hide: !this.clearable })}"
        @click="${this.handleClear}"
      ></slot>
      <slot
        name="reveal-icon"
        class="${classMap({ hide: !this.revealPassword })}"
        @click="${this.handleRevealPassword}"
      ></slot>
    `;
  }

  get input() {
    const slot = this.shadowRoot.querySelector(`slot[name='input']`);
    return slot?.assignedNodes()[0];
  }

  get errorSlot() {
    const slot = this.shadowRoot.querySelector(`slot[name='error']`);
    return slot?.assignedNodes()[0];
  }

  render() {
    return html`
      <slot name="input-icon"></slot>
      <slot name="label"></slot>
      ${(this.icon === 'left' && this.iconTemplate()) || ''}
      <slot
        name="input"
        @change="${this.onChange}"
        @input="${this.onChange}"
        @animationstart="${this.onAutoFill}"
        @focusin="${this.onFocus}"
        @focusout="${this.onFocusOut}"
      ></slot>
      ${(this.icon === 'right' && this.iconTemplate()) || ''}
      <slot name="error-icon"></slot>
      <slot name="error"></slot>
      <slot name="hint"></slot>
    `;
  }

  static styles = css`
    :host {
      position: relative;
      display: block;
      height: var(--hh-height, 50px);
      transition: border var(--hh-duration-default, 300ms);
      background: var(--hh-background, transparent);
      border: var(--hh-border-size, 1px) solid var(--hh-border-color, #707070);
      border-radius: var(--hh-border-radius, 0);
    }

    ::slotted(label) {
      position: absolute;
      transform: translateY(17px);
      transition:
        transform var(--hh-duration-default, 300ms),
        font-size var(--hh-duration-default, 300ms);
    }

    ::slotted(label),
    ::slotted(input),
    ::slotted(textarea),
    ::slotted(select) {
      font: var(--hh-font);
      color: var(--hh-hint-color, #707070);
      width: 100%;
      position: absolute;
      padding: var(--hh-padding, 0);
      text-indent: 20px;
    }

    ::slotted(input),
    ::slotted(textarea),
    ::slotted(select) {
      color: var(--hh-color, #000);
      z-index: 2;
      height: 100%;
      background: transparent;
      border: none;
      margin: var(--hh-input-padding, 10px 0);
    }

    ::slotted(input:-webkit-autofill) {
      animation-name: onAutoFillStart;
    }

    ::slotted(input::-webkit-calendar-picker-indicator) {
      margin-left: -15px;
    }

    ::slotted(input:not(:-webkit-autofill)) {
      animation-name: onAutoFillCancel;
    }

    ::slotted(input:focus-visible),
    ::slotted(textarea:focus-visible) {
      outline: none !important;
    }

    ::slotted(input[type='date']) {
      text-indent: 10px;
    }

    ::slotted(select) {
      appearance: none;
    }

    :host([focused]) ::slotted(label),
    :host([filled]) ::slotted(label),
    ::slotted(label.is-date) {
      color: var(--hh-hint-color, #707070);
      font: var(--hh-hint-font);
      transform: translateY(5px);
      transition:
        transform var(--hh-duration-default, 300ms),
        font-size var(--hh-duration-default, 300ms);
    }

    ::slotted(label.is-date) {
      transform: translateY(12px) !important;
    }

    :host([animation='disappear'][focused]) ::slotted(label),
    :host([animation='disappear'][filled]) ::slotted(label) {
      visibility: hidden;
      transform: translateY(10px);
      font-size: inherit;
    }

    :host(:hover) {
      background: var(--hh-background-hover, #f1f1f1);
      cursor: pointer;
      border-color: var(--hh-border-color-hover, #707070);
    }

    :host(:not([disabled]):hover) ::slotted(label) {
      color: var(--hh-color, #000);
    }

    :host([focused]) {
      outline: 2px solid var(--hh-color-focus, #2644ab);
    }

    :host([disabled]) ::slotted(label),
    :host([disabled]) ::slotted(textarea),
    :host([disabled]) ::slotted(input),
    :host([disabled]) ::slotted(select) {
      color: var(--hh-disabled-color, #dddad4);
    }

    :host([disabled]) {
      border: var(--hh-border-size, 1px) solid var(--hh-disabled-color, #dddad4);
      background: var(--hh-disabled-background, transparent);
      color: var(--hh-disabled-color, #dddad4);
    }

    :host([disabled]) ::slotted(input),
    :host([disabled]) ::slotted(textarea),
    :host([disabled]) ::slotted(select) {
      cursor: not-allowed;
    }

    :host([resize]) ::slotted(textarea) {
      resize: none;
      background-image: var(--hh-textarea-svg);
      background-repeat: no-repeat;
      background-position: calc(100% - 0.2rem) calc(100% - 0.4rem);
    }

    :host([error]) {
      border: 2px solid var(--hh-color-error, #ec0000);
    }

    :host([error]) {
      margin-bottom: 14px;
    }

    :host([theme='minimal']) {
      background: transparent;
      border: none;
      border-bottom: var(--hh-border-size, 1px) solid currentColor;
    }

    :host([icon='left']) ::slotted(label) {
      padding-left: var(--hh-left-indent, 32px);
    }

    :host([icon='left']) ::slotted(input) {
      text-indent: var(--hh-left-indent, 32px);
    }

    :host([icon='right']) ::slotted(label) {
      padding-right: var(--hh-right-indent, 32px);
    }

    ::slotted([slot='icon']),
    ::slotted([slot='clear-icon']),
    ::slotted([slot='reveal-icon']) {
      display: flex;
      align-items: center;
      justify-content: center;
      position: absolute;
      width: var(--hh-icon-width, 23px);
      height: var(--hh-height, 50px);
      z-index: 3;
      appearance: none;
      background-color: transparent;
      border: none;
      padding: 0;
      color: currentColor;
      top: var(--hh-icon-top-offset, 0);
    }

    ::slotted([slot='icon']),
    ::slotted([slot='clear-icon']),
    ::slotted([slot='reveal-icon']) {
      cursor: pointer;
    }

    :host([icon='right']) ::slotted([slot='icon']),
    :host([icon='right']) ::slotted([slot='clear-icon']),
    :host([icon='right']) ::slotted([slot='reveal-icon']) {
      right: var(--hh-icon-right, 10px);
    }

    :host([icon='right'][clearable][reveal-password]) ::slotted([slot='clear-icon']) {
      right: var(--hh-icon-clear-right, 33px);
    }

    :host(:not([filled])) ::slotted([slot='clear-icon']) {
      display: none;
    }

    :host([validate]) ::slotted([slot='icon']),
    :host([validate]) ::slotted([slot='clear-icon']),
    :host([validate]) ::slotted([slot='reveal-icon']) {
      display: none;
    }

    :host([validate][success]) ::slotted([slot='icon']),
    :host([validate][success]) ::slotted([slot='clear-icon']),
    :host([validate][success]) ::slotted([slot='reveal-icon']) {
      display: flex;
    }

    :host([height='multiline']),
    :host([height='multiline']) ::slotted(textarea) {
      height: var(--hh-multiline-height, 100px);
    }

    :host([height='compact']),
    :host([height='compact']) ::slotted(input),
    :host([height='compact']) ::slotted(label),
    :host([height='compact']) ::slotted([slot='icon']) {
      height: var(--hh-compact-height, 35px);
    }

    :host([height='compact']) ::slotted(label) {
      line-height: var(--hh-compact-height, 35px);
    }

    ::slotted([slot='hint']),
    ::slotted([slot='error']),
    ::slotted([slot='error-icon']) {
      position: var(--hh-hint-position, absolute);
      inset: 85% 0 0;
      margin-top: var(--hh-hint-margin-top, 0.8rem);
      font: var(--hh-hint-font);
      translate: var(--hh-hint-translate);
      display: flex;
    }

    ::slotted([slot='error']) {
      color: var(--hh-color-error, var(--hh-color-danger, #ec0000));
      translate: 18px 2px;
    }

    ::slotted([slot='error-icon']) {
      height: 14px;
      width: 14px;
      translate: 0 -1px;
    }

    :host(.search-input) {
      padding-left: 25px;
      background: #f1f1f1;
    }

    :host(.search-input) ::slotted(input) {
      width: calc(100% - 25px);
    }

    :host(.search-input) ::slotted(label) {
      width: calc(100% - 25px);
    }

    ::slotted([slot='input-icon']) {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      left: 10px;
    }

    .hide {
      display: none;
    }
  `;
}

customElements.define('hh-input-wrapper', HHInputWrapper);
